import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getListingById, updateListing, type ListingDetail } from '@/lib/queries/listings'

// Minimal portable-text -> plain text
const portableTextToPlain = (desc: any): string => {
  if (!desc) return ''
  if (typeof desc === 'string') return desc
  if (Array.isArray(desc)) {
    return desc
      .map((block: any) => {
        if (block?._type === 'block' && Array.isArray(block.children)) {
          return block.children.map((c: any) => c?.text ?? '').join('')
        }
        return ''
      })
      .join('\n')
  }
  return ''
}

function EditListingForm({ listing, id }: { listing: ListingDetail; id: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [title, setTitle] = useState(listing.title || '')
  const [price, setPrice] = useState<string>(String(listing.price ?? ''))
  const [status, setStatus] = useState<ListingDetail['status']>(listing.status || 'not_synced')
  const [description, setDescription] = useState<string>(portableTextToPlain(listing.description)) // plain text editing

  const { mutateAsync: doUpdate, isPending } = useMutation({
    mutationFn: (payload: { title: string; price: number; status: any; description: any[] }) =>
      updateListing(id, payload),
  })

  const onSave = async () => {
    // Normalize title in case UI libraries append to the initial value rather than replacing it.
    const baseTitle = listing?.title || ''
    let normalizedTitle = title.trim()
    if (baseTitle && title.startsWith(baseTitle)) {
      const suffix = title.slice(baseTitle.length).trim()
      if (suffix.length > 0) {
        normalizedTitle = suffix
      }
    }
    const payload = {
      title: normalizedTitle,
      price: price ? parseFloat(price) : undefined,
      status,
      // Map plain text to minimal portable text
      description:
        description.trim().length > 0
          ? [
              {
                _type: 'block',
                style: 'normal',
                markDefs: [],
                children: [
                  {
                    _type: 'span',
                    text: description.trim(),
                    marks: [],
                  },
                ],
              },
            ]
          : [],
    }
    await doUpdate(payload as any)
    // Update cache immediately for snappy UX
    queryClient.setQueryData(['listing', id], (prev: any) => {
      if (!prev) return prev
      return {
        ...prev,
        title: payload.title ?? prev.title,
        price: payload.price ?? prev.price,
        status: payload.status ?? prev.status,
        description: payload.description ?? prev.description,
      }
    })
    // Invalidate relevant queries to refetch fresh data
    await queryClient.invalidateQueries({ queryKey: ['listing', id] })
    await queryClient.invalidateQueries({ queryKey: ['listings'] })
    navigate(`/listings/${id}`)
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4">
          <h1 className="text-xl font-bold text-gray-900">Edit Listing</h1>
        </div>
      </header>

      <main className="p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="title">
            Title
          </label>
          <input
            id="title"
            aria-label="Title"
            type="text"
            className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={listing.title || 'Untitled Listing'}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="price">
            Price
          </label>
          <div className="flex items-center gap-2">
            <span className="text-lg">£</span>
            <input
              id="price"
              aria-label="Price"
              type="number"
              step="0.01"
              className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder={String(listing.price ?? '')}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="status">
            Status
          </label>
          <select
            id="status"
            className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
          >
            <option value="not_synced">Draft</option>
            <option value="synced">Active</option>
            <option value="pending">Pending</option>
            <option value="error">Error</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="description">
            Description
          </label>
          <textarea
            id="description"
            rows={4}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description override..."
          />
        </div>
      </main>

      <footer className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-3">
        <button
          className="flex-1 bg-gray-100 text-gray-900 py-3 px-4 rounded-lg font-medium touch-target-min-44"
          onClick={() => navigate(`/listings/${id}`)}
        >
          Cancel
        </button>
        <button
          className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium touch-target-min-44 disabled:opacity-50"
          onClick={onSave}
          disabled={isPending}
        >
          Save
        </button>
      </footer>
    </div>
  )
}

export default function EditListing() {
  const { id } = useParams<{ id: string }>()
  const { data: listing, isLoading } = useQuery({
    queryKey: ['listing', id],
    queryFn: () => getListingById(id!),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  })

  if (isLoading || !listing) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-gray-500">Loading listing...</div>
      </div>
    )
  }

  return <EditListingForm listing={listing} id={id!} />
}

